import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../utils/classMerger";

// Unlike react-colorful's HexColorInput, this never treats a three-digit
// shorthand as finished mid-typing and never rewrites its draft while focused;
// it commits only a six-digit code and tidies the value on blur.

const NOT_HEX = /[^0-9a-f]/gi;

/** Hex digits only, uppercase, at most six: "#a0-87 47x" → "A08747". */
export const cleanHexDraft = (raw: string): string =>
    raw.replace(NOT_HEX, "").toUpperCase().slice(0, 6);

/** Six digits as they are, three doubled ("ABC" → "#AABBCC"), else null. */
export const draftToHex = (draft: string): string | null => {
    if (draft.length === 6) return `#${draft}`;
    if (draft.length === 3) {
        return `#${draft
            .split("")
            .map((digit) => digit + digit)
            .join("")}`;
    }
    return null;
};

interface HexCodeInputProps {
    /** The committed colour ("#RRGGBB"). */
    color: string;
    /** Fired only with a finished "#RRGGBB" code. */
    onChange: (hex: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    className?: string;
    id?: string;
    "aria-label"?: string;
}

const HexCodeInput: React.FC<HexCodeInputProps> = ({
    color,
    onChange,
    onBlur,
    disabled = false,
    className,
    id,
    "aria-label": ariaLabel,
}) => {
    const [draft, setDraft] = useState(() => cleanHexDraft(color));
    const editing = useRef(false);
    const selectOnMouseUp = useRef(false);
    // The last code handed to onChange (or received through the prop), so a
    // blur right after the sixth digit does not commit the same code twice.
    const committed = useRef(`#${cleanHexDraft(color)}`);

    // A picker drag changes the colour underneath the box: follow it, but
    // never while the user is typing.
    useEffect(() => {
        committed.current = `#${cleanHexDraft(color)}`;
        if (!editing.current) setDraft(cleanHexDraft(color));
    }, [color]);

    const commit = (hex: string): void => {
        if (hex === committed.current) return;
        committed.current = hex;
        onChange(hex);
    };

    // Closing a popover or dialog removes the box before its blur can fire, so
    // a finished draft still in it (e.g. FFF) is committed on the way out.
    const flushDraft = useRef<() => void>(() => undefined);
    flushDraft.current = () => {
        const hex = draftToHex(draft);
        if (editing.current && hex) commit(hex);
    };
    useEffect(() => () => flushDraft.current(), []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const next = cleanHexDraft(event.target.value);
        setDraft(next);
        if (next.length === 6) commit(`#${next}`);
    };

    // A pasted code replaces the whole value, wherever the cursor sits.
    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
        const pasted = cleanHexDraft(event.clipboardData?.getData("text") ?? "");
        if (pasted.length !== 6 && pasted.length !== 3) return;
        event.preventDefault();
        setDraft(pasted);
        if (pasted.length === 6) commit(`#${pasted}`);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
        editing.current = true;
        event.target.select();
        selectOnMouseUp.current = true;
    };

    // Fires before focus, so it only clears the flag for a click on a box that
    // is already focused (e.g. reached with Tab): that click places the cursor.
    const handleMouseDown = (): void => {
        selectOnMouseUp.current = false;
    };

    // The mouse-up that follows a click would collapse the selection made on
    // focus; swallow that one so a click-then-type replaces the code.
    const handleMouseUp = (event: React.MouseEvent<HTMLInputElement>): void => {
        if (selectOnMouseUp.current) {
            event.preventDefault();
            selectOnMouseUp.current = false;
        }
    };

    const handleBlur = (): void => {
        editing.current = false;
        selectOnMouseUp.current = false;
        const hex = draftToHex(draft);
        if (hex) {
            setDraft(hex.slice(1));
            commit(hex);
        } else {
            setDraft(cleanHexDraft(color));
        }
        onBlur?.();
    };

    return (
        <input
            id={id}
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            aria-label={ariaLabel}
            disabled={disabled}
            value={`#${draft}`}
            onChange={handleChange}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onBlur={handleBlur}
            className={cn("font-mono uppercase", className)}
        />
    );
};

export default HexCodeInput;
